import React from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiMessageSquare,
  FiFileText,
  FiDollarSign,
  FiPackage,
  FiCreditCard,
  FiMapPin,
  FiBarChart2,
  FiCheckSquare,
  FiCheckCircle,
} from 'react-icons/fi';

interface SidebarProps {
  onClose?: () => void;
}

interface NavItem {
  name: string;
  icon: any;
  path: string;
  badge?: string;
  badgeColor?: string;
}

const navItems: NavItem[] = [
  {
    name: 'AI Assistant',
    icon: FiMessageSquare,
    path: '/',
    badge: 'NEW',
    badgeColor: 'executive',
  },
  {
    name: 'Document Library',
    icon: FiFileText,
    path: '/documents',
  },
  {
    name: 'Quotes',
    icon: FiDollarSign,
    path: '/quotes',
    badge: '2',
    badgeColor: 'professional',
  },
  {
    name: 'Orders',
    icon: FiPackage,
    path: '/orders',
    badge: '1',
    badgeColor: 'anduril',
  },
  {
    name: 'Invoices',
    icon: FiCreditCard,
    path: '/invoices',
  },
  {
    name: 'Locations',
    icon: FiMapPin,
    path: '/locations',
  },
  {
    name: 'Progress Reports',
    icon: FiBarChart2,
    path: '/progress-reports',
  },
  {
    name: 'Punchlists',
    icon: FiCheckSquare,
    path: '/punchlists',
    badge: '3',
    badgeColor: 'urgent',
  },
  {
    name: 'Sign Offs',
    icon: FiCheckCircle,
    path: '/sign-offs',
    badge: '1',
    badgeColor: 'contract',
  },
];

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const bg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.200', 'navy.700');
  const textColor = useColorModeValue('secondaryGray.700', 'white');
  const logoColor = useColorModeValue('brand.500', 'white');

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onClose) onClose();
  };

  return (
    <Box
      position="fixed"
      left="0"
      top="0"
      w="280px"
      h="100vh"
      bg={bg}
      borderRight="1px solid"
      borderColor={borderColor}
      zIndex="sticky"
      boxShadow="0px 2px 8px rgba(0, 0, 0, 0.04)"
    >
      <Flex direction="column" h="full">
        {/* Logo Section */}
        <Box p="32px 24px 24px 24px">
          <HStack spacing="12px">
            <Box
              w="40px"
              h="40px"
              bg="white"
              borderRadius="8px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              boxShadow="sm"
              p="4px"
            >
              <img
                src="/tangram-logo.png"
                alt="Tangram Interiors Logo"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  // Fallback to text if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = '<div style="color: #334e68; font-size: 20px; font-weight: bold; font-family: Inter, sans-serif;">T</div>';
                    parent.style.backgroundColor = '#334e68';
                    parent.querySelector('div')!.style.color = 'white';
                  }
                }}
              />
            </Box>
            <VStack spacing="0" align="start">
              <Text
                color={logoColor}
                fontSize="xl"
                fontWeight="bold"
                lineHeight="1"
              >
                Tangram Interiors
              </Text>
              <Text
                color={textColor}
                fontSize="sm"
                fontWeight="medium"
                lineHeight="1"
              >
                Vendor Portal
              </Text>
            </VStack>
          </HStack>
        </Box>

        <Divider />

        {/* Navigation */}
        <Box flex="1" p="24px">
          <VStack spacing="4px" align="stretch">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <NavButton
                  key={item.path}
                  item={item}
                  isActive={isActive}
                  onClick={() => handleNavigation(item.path)}
                />
              );
            })}
          </VStack>
        </Box>

        {/* Footer */}
        <Box p="24px" borderTop="1px solid" borderColor={borderColor}>
          <VStack spacing="8px" align="start">
            <Text fontSize="xs" color={textColor} fontWeight="medium">
              Project: Corporate HQ Renovation
            </Text>
            <HStack spacing="8px">
              <Box w="2" h="2" bg="contract.500" borderRadius="full" />
              <Text fontSize="xs" color="contract.600" fontWeight="semibold">
                65% Complete
              </Text>
            </HStack>
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};

interface NavButtonProps {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({ item, isActive, onClick }) => {
  const activeBg = useColorModeValue('brand.50', 'brand.900');
  const activeColor = useColorModeValue('brand.600', 'brand.200');
  const hoverBg = useColorModeValue('secondaryGray.100', 'navy.700');
  const textColor = useColorModeValue('secondaryGray.700', 'white');

  return (
    <Flex
      align="center"
      p="12px 16px"
      borderRadius="8px"
      cursor="pointer"
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeColor : textColor}
      _hover={{
        bg: isActive ? activeBg : hoverBg,
      }}
      transition="all 0.2s ease-in-out"
      onClick={onClick}
    >
      <Icon as={item.icon} w="20px" h="20px" />
      <Text ml="12px" fontSize="sm" fontWeight="medium" flex="1">
        {item.name}
      </Text>
      {item.badge && (
        <Badge
          colorScheme={item.badgeColor || 'gray'}
          fontSize="10px"
          borderRadius="full"
          px="6px"
          py="2px"
        >
          {item.badge}
        </Badge>
      )}
    </Flex>
  );
};

export default Sidebar; 